import React, { useEffect, useState, useCallback } from 'react';
import { Users, Camera, Activity, ArrowUpRight, Search, RefreshCw, ChevronDown, ChevronRight, ShieldAlert, Lock, MapPin } from 'lucide-react';
import { Button } from './ui/button';

interface ClientCamera {
  id: string;
  name: string;
  location: string;
  status: string;
  is_online: boolean;
  assigned_usecase: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  organization_type: string;
  camera_count: number;
  online_camera_count: number;
  health: number;
  alert_count: number;
  status: string;
  created_at: string | null;
  cameras: ClientCamera[];
}

export function CRM() {
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [totals, setTotals] = useState({ total_clients: 0, total_cameras: 0, total_online_cameras: 0 });
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState('');
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/admin/clients', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }

      if (!response.ok) {
        setError('Failed to load client data.');
        return;
      }

      const data = await response.json();
      setClients(data.clients || []);
      setTotals({
        total_clients: data.total_clients || 0,
        total_cameras: data.total_cameras || 0,
        total_online_cameras: data.total_online_cameras || 0
      });
    } catch (e) {
      setError('Failed to load client data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  if (accessDenied) {
    return (
      <div className="crm-portal flex flex-col items-center justify-center py-32 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <Lock className="w-7 h-7 text-red-400" />
        </div>
        <h2 className="text-sm font-black uppercase tracking-widest text-white mb-2">Access Restricted</h2>
        <p className="text-xs text-blue-100/40 max-w-sm">Strategic CRM is only available to super-admin accounts.</p>
      </div>
    );
  }

  const filteredClients = clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const avgHealth = clients.length ? Math.round(clients.reduce((sum, c) => sum + c.health, 0) / clients.length) : 0;
  const needsAttention = clients.filter(c => c.health < 70 || c.camera_count === 0);

  return (
    <div className="crm-portal">
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <i style={{ color: 'var(--vg)', fontStyle: 'normal' }}>⬡</i> Customer Intelligence Center
      </h2>

      <div className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <div className="eg-panel crm-kpi">
          <div className="eg-panel-body" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="k-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(34, 211, 238, .1)', color: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} />
            </div>
            <div className="k-data">
              <span style={{ fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase' }}>Active Organizations</span>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>{totals.total_clients}</div>
            </div>
          </div>
        </div>

        <div className="eg-panel crm-kpi">
          <div className="eg-panel-body" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="k-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(146, 77, 191, .1)', color: 'var(--vg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Camera size={20} />
            </div>
            <div className="k-data">
              <span style={{ fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase' }}>Total Cameras</span>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>{totals.total_cameras}</div>
            </div>
          </div>
        </div>

        <div className="eg-panel crm-kpi">
          <div className="eg-panel-body" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="k-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255, 77, 141, .1)', color: 'var(--mag)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={20} />
            </div>
            <div className="k-data">
               <span style={{ fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase' }}>Avg System Health</span>
               <div style={{ fontSize: '24px', fontWeight: 600 }}>{avgHealth}%</div>
            </div>
          </div>
        </div>

        <div className="eg-panel crm-kpi">
          <div className="eg-panel-body" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="k-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(251, 191, 36, .1)', color: 'var(--yellow)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldAlert size={20} />
            </div>
            <div className="k-data">
               <span style={{ fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase' }}>Needs Attention</span>
               <div style={{ fontSize: '24px', fontWeight: 600 }}>{needsAttention.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '24px' }}>
        <div className="eg-panel">
          <div className="eg-panel-hdr" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <h3>Customer Intelligence Database</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: 'var(--t2)' }} />
                <input type="text" placeholder="Search accounts..." style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', padding: '5px 10px 5px 28px', borderRadius: '6px', fontSize: '12px' }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <button onClick={fetchClients} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--t1)', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} className="hover:border-violet-500 hover:text-violet-400 transition-colors">
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
          </div>
          <div className="eg-panel-body" style={{ padding: 0 }}>
            {error && <div style={{ padding: '20px', fontSize: '12px', color: 'var(--mag)' }}>{error}</div>}
            {!error && !loading && filteredClients.length === 0 && (
              <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: '12px', color: 'var(--t2)' }}>No client accounts found.</div>
            )}
            {!error && filteredClients.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Organization</th>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cameras</th>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Health</th>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Alerts</th>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ padding: '12px 20px', fontSize: '11px', color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map(c => (
                    <React.Fragment key={c.id}>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', transition: 'background 0.2s', cursor: 'pointer' }} className="hover:bg-white/5" onClick={() => setExpandedClientId(expandedClientId === c.id ? null : c.id)}>
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>{c.name}</span>
                            <span style={{ fontSize: '11px', color: 'var(--t2)' }}>{c.email}</span>
                          </div>
                        </td>
                        <td style={{ padding: '14px 20px', fontSize: '13px', color: 'var(--t1)' }}>{c.camera_count} Nodes ({c.online_camera_count} online)</td>
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: c.health > 90 ? 'var(--green)' : c.health > 70 ? 'var(--yellow)' : 'var(--mag)' }}>{c.health}%</span>
                            <div style={{ flex: 1, minWidth: '60px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
                              <div style={{ height: '100%', width: `${c.health}%`, background: c.health > 90 ? 'var(--green)' : c.health > 70 ? 'var(--yellow)' : 'var(--mag)', borderRadius: '2px' }}></div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 20px', fontSize: '13px', fontWeight: 500, color: '#fff' }}>{c.alert_count}</td>
                        <td style={{ padding: '14px 20px' }}>
                          <span className={`eg-pill ${c.status === 'active' ? 'live' : 'warn'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 20px' }}>
                           <button style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--t1)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} className="hover:border-violet-500 hover:text-violet-400 transition-colors">
                              {expandedClientId === c.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Cameras
                           </button>
                        </td>
                      </tr>
                      {expandedClientId === c.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: '0 20px 20px', background: 'rgba(0,0,0,0.2)' }}>
                            {c.cameras.length === 0 ? (
                              <div style={{ padding: '16px 0', fontSize: '12px', color: 'var(--t2)' }}>No cameras registered for this client.</div>
                            ) : (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', paddingTop: '14px' }}>
                                {c.cameras.map(cam => (
                                  <div key={cam.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>{cam.name}</span>
                                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: cam.is_online ? 'var(--green)' : 'var(--mag)' }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--t2)' }}>
                                      <MapPin size={10} /> {cam.location || 'Unknown location'}
                                    </div>
                                    <div style={{ fontSize: '10px', color: 'var(--t2)', textTransform: 'uppercase', marginTop: '4px' }}>{cam.assigned_usecase}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="eg-panel" style={{ flex: 1, background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)' }}>
            <div className="eg-panel-hdr" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--vg)' }}>
                <ShieldAlert size={14} /> Clients Needing Attention
              </h3>
            </div>
            <div className="eg-panel-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {needsAttention.length === 0 && (
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--t2)' }}>All clients are healthy.</p>
              )}
              {needsAttention.map((c) => (
                <div key={c.id} style={{ padding: '12px', background: 'rgba(0,0,0,0.4)', borderRadius: '10px', borderLeft: `3px solid ${c.camera_count === 0 ? 'var(--cyan)' : 'var(--mag)'}` }}>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--t1)', lineHeight: '1.5' }}>
                    {c.camera_count === 0 ? `${c.name} has no cameras configured.` : `${c.name} system health at ${c.health}% (${c.online_camera_count}/${c.camera_count} cameras online).`}
                  </p>
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: 'var(--t2)', textTransform: 'uppercase' }}>{c.organization_type}</span>
                    <ArrowUpRight size={14} style={{ color: 'var(--vg)', cursor: 'pointer' }} onClick={() => setExpandedClientId(c.id)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
